import { Injectable, inject } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { get } from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  Query,
  TrackerBloodPressureQueriesFindAllArgs,
  TrackerBloodPressureQueryResult,
  TrackerCaloriesQueriesFindAllArgs,
  TrackerCaloriesQueryResult,
  TrackerDistanceQueriesFindAllArgs,
  TrackerDistanceQueryResult,
  TrackerHeartRateQueriesFindAllArgs,
  TrackerHeartRateQueryResult,
  TrackerMutationsSyncArgs,
  TrackerOxygenSaturationQueriesFindAllArgs,
  TrackerOxygenSaturationQueryResult,
  TrackerSetting,
  TrackerSleepQueriesFindAllArgs,
  TrackerSleepQueryResult,
  TrackerSource,
  TrackerStepsQueriesFindAllArgs,
  TrackerStepsQueryResult
} from '../../../../interfaces/schema';

/**
 * Tracker Sync Service.
 *
 * @exports
 * @class TrackerService
 */
@Injectable()
export class TrackerSyncService {
  private readonly apollo = inject(Apollo);

  /**
   * @description Find all tracker blood pressure.
   * @param {TrackerBloodPressureQueriesFindAllArgs} [variables] Params.
   * @returns {Observable<TrackerBloodPressureQueryResult>}
   * @memberof TrackerSyncService
   */
  findAllTrackerBlooodPressure(
    variables?: TrackerBloodPressureQueriesFindAllArgs
  ): Observable<TrackerBloodPressureQueryResult> {
    const query = gql`
      query TrackerBloodPressure(
        $sort: TrackerBloodPressureQuerySorter
        $where: TrackerBloodPressureQueryFilter
        $limit: Int
        $offset: Int
      ) {
        Tracker {
          BloodPressure {
            findAll(
              sort: $sort
              where: $where
              limit: $limit
              offset: $offset
            ) {
              count
              results {
                id
                unit
                value {
                  systolic
                  diastolic
                }
                source
                endDate
                startDate
                createdAt
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query<Query>({ query, variables })
      .pipe(
        map(
          ({ data }) =>
            get(
              data,
              'Tracker.BloodPressure.findAll'
            ) as TrackerBloodPressureQueryResult
        )
      );
  }

  /**
   * @description Find all tracker calories.
   * @param {TrackerCaloriesQueriesFindAllArgs} [variables] Params.
   * @returns {Observable<TrackerCaloriesQueryResult>}
   * @memberof TrackerSyncService
   */
  findAllTrackerCalories(
    variables?: TrackerCaloriesQueriesFindAllArgs
  ): Observable<TrackerCaloriesQueryResult> {
    const query = gql`
      query TrackerCalories(
        $sort: TrackerCaloriesQuerySorter
        $where: TrackerCaloriesQueryFilter
        $limit: Int
        $offset: Int
      ) {
        Tracker {
          Calories {
            findAll(
              sort: $sort
              where: $where
              limit: $limit
              offset: $offset
            ) {
              count
              results {
                id
                unit
                value
                source
                endDate
                startDate
                createdAt
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query<Query>({ query, variables })
      .pipe(
        map(
          ({ data }) =>
            get(data, 'Tracker.Calories.findAll') as TrackerCaloriesQueryResult
        )
      );
  }

  /**
   * @description Find all tracker distance.
   * @param {TrackerDistanceQueriesFindAllArgs} [variables] Params.
   * @returns {Observable<TrackerDistanceQueryResult>}
   * @memberof TrackerSyncService
   */
  findAllTrackerDistance(
    variables?: TrackerDistanceQueriesFindAllArgs
  ): Observable<TrackerDistanceQueryResult> {
    const query = gql`
      query TrackerDistances(
        $sort: TrackerDistanceQuerySorter
        $where: TrackerDistanceQueryFilter
        $limit: Int
        $offset: Int
      ) {
        Tracker {
          Distance {
            findAll(
              sort: $sort
              where: $where
              limit: $limit
              offset: $offset
            ) {
              count
              results {
                id
                unit
                value
                source
                endDate
                startDate
                createdAt
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query<Query>({ query, variables })
      .pipe(
        map(
          ({ data }) =>
            get(data, 'Tracker.Distance.findAll') as TrackerDistanceQueryResult
        )
      );
  }

  /**
   * @description Find all tracker heart rate.
   * @param {TrackerHeartRateQueriesFindAllArgs} [variables] Params.
   * @returns {Observable<TrackerHeartRateQueryResult>}
   * @memberof TrackerSyncService
   */
  findAllTrackerHeartRate(
    variables?: TrackerHeartRateQueriesFindAllArgs
  ): Observable<TrackerHeartRateQueryResult> {
    const query = gql`
      query TrackerHeartRates(
        $sort: TrackerHeartRateQuerySorter
        $where: TrackerHeartRateQueryFilter
        $limit: Int
        $offset: Int
      ) {
        Tracker {
          HeartRate {
            findAll(
              sort: $sort
              where: $where
              limit: $limit
              offset: $offset
            ) {
              count
              results {
                id
                unit
                value
                source
                endDate
                startDate
                createdAt
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query<Query>({ query, variables })
      .pipe(
        map(
          ({ data }) =>
            get(
              data,
              'Tracker.HeartRate.findAll'
            ) as TrackerHeartRateQueryResult
        )
      );
  }

  /**
   * @description Find all tacker oxygen saturation.
   * @param {TrackerOxygenSaturationQueriesFindAllArgs} [variables] Params.
   * @returns {Observable<TrackerOxygenSaturationQueryResult>}
   * @memberof TrackerSyncService
   */
  findAllTrackerOxygenSaturation(
    variables?: TrackerOxygenSaturationQueriesFindAllArgs
  ): Observable<TrackerOxygenSaturationQueryResult> {
    const query = gql`
      query TrackerOxygenSaturations(
        $sort: TrackerOxygenSaturationQuerySorter
        $where: TrackerOxygenSaturationQueryFilter
        $limit: Int
        $offset: Int
      ) {
        Tracker {
          OxygenSaturation {
            findAll(
              sort: $sort
              where: $where
              limit: $limit
              offset: $offset
            ) {
              count
              results {
                id
                unit
                value
                source
                endDate
                startDate
                createdAt
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query<Query>({ query, variables })
      .pipe(
        map(
          ({ data }) =>
            get(
              data,
              'Tracker.OxygenSaturation.findAll'
            ) as TrackerOxygenSaturationQueryResult
        )
      );
  }

  /**
   * @description Find all tracker sleep.
   * @param {TrackerStepsQueriesFindAllArgs} [variables] Params.
   * @returns {Observable<TrackerSleepQueryResult>}
   * @memberof TrackerSyncService
   */
  findAllTrackerSleep(
    variables?: TrackerSleepQueriesFindAllArgs
  ): Observable<TrackerSleepQueryResult> {
    const query = gql`
      query TrackerSleeps(
        $sort: TrackerSleepQuerySorter
        $where: TrackerSleepQueryFilter
        $limit: Int
        $offset: Int
      ) {
        Tracker {
          Sleep {
            findAll(
              sort: $sort
              where: $where
              limit: $limit
              offset: $offset
            ) {
              count
              results {
                id
                unit
                value
                source
                endDate
                startDate
                createdAt
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query<Query>({ query, variables })
      .pipe(
        map(
          ({ data }) =>
            get(data, 'Tracker.Sleep.findAll') as TrackerSleepQueryResult
        )
      );
  }

  /**
   * @description Find all tracker steps.
   * @param {TrackerStepsQueriesFindAllArgs} [variables] Params.
   * @returns {Observable<TrackerStepsQueryResult>}
   * @memberof TrackerSyncService
   */
  findAllTrackerSteps(
    variables?: TrackerStepsQueriesFindAllArgs
  ): Observable<TrackerStepsQueryResult> {
    const query = gql`
      query TrackerSteps(
        $sort: TrackerStepsQuerySorter
        $where: TrackerStepsQueryFilter
        $limit: Int
        $offset: Int
      ) {
        Tracker {
          Steps {
            findAll(
              sort: $sort
              where: $where
              limit: $limit
              offset: $offset
            ) {
              count
              results {
                id
                unit
                value
                source
                endDate
                startDate
                createdAt
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query<Query>({ query, variables })
      .pipe(
        map(
          ({ data }) =>
            get(data, 'Tracker.Steps.findAll') as TrackerStepsQueryResult
        )
      );
  }

  /**
   * @description Find tracker setting.
   * @param {{ source?: TrackerSource | null }} [variables] Params.
   * @param {TrackerSource} variables.source Tracker source.
   * @returns {Observable<TrackerSetting>}
   * @memberof TrackerSyncService
   */
  findTrackerSetting(variables?: {
    source?: TrackerSource | null;
  }): Observable<TrackerSetting> {
    const query = gql`
      query TrackerSetting($source: TrackerSource) {
        Tracker {
          Setting {
            find(source: $source) {
              id
              types
              source
              enable
              createdAt
              connectedAt
            }
          }
        }
      }
    `;
    return this.apollo
      .query<Query>({ query, variables })
      .pipe(
        map(({ data }) => get(data, 'Tracker.Setting.find') as TrackerSetting)
      );
  }

  /**
   * @description Sync tracker.
   * @param {TrackerMutationsSyncArgs} [variables] Params.
   * @returns {Observable<boolean>}
   * @memberof TrackerSyncService
   */
  syncTracker(variables?: TrackerMutationsSyncArgs): Observable<boolean> {
    const mutation = gql`
      mutation SyncTracker($form: [TrackerSyncForm]) {
        Tracker {
          sync(form: $form)
        }
      }
    `;
    return this.apollo
      .mutate<any>({ mutation, variables, fetchPolicy: 'no-cache' })
      .pipe(map(({ data }) => get(data, 'Tracker.sync') as boolean));
  }
}
